import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Image, Video, Wrench } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { Button } from '@/shared/ui/buttons/Button';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { extractErrorMessage } from '@/shared/api/errors';
import { useReports, useReportDone, useReportCancel } from '@/entities/reports';
import { usePerm } from '@/shared/hooks/usePerm';
import { useToast } from '@/shared/hooks/useToast';
import { formatUsd } from '@/shared/utils/currency';
import { formatDateTime } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import type {
  ApiServiceReport,
  ReportStatus,
  ReportType,
} from '@/shared/types/api/reports';

type Props = {
  type: ReportType;
};

const TITLE_KEYS: Record<ReportType, { title: string; subtitle: string }> = {
  PHOTO: {
    title: 'serviceReports.photo.title',
    subtitle: 'serviceReports.photo.subtitle',
  },
  VIDEO: {
    title: 'serviceReports.video.title',
    subtitle: 'serviceReports.video.subtitle',
  },
  PROVERKA: {
    title: 'serviceReports.proverka.title',
    subtitle: 'serviceReports.proverka.subtitle',
  },
};

const STATUS_FILTERS: { value: ReportStatus | undefined; labelKey: string }[] = [
  { value: undefined, labelKey: 'serviceReports.filter.all' },
  { value: 'PENDING', labelKey: 'serviceReports.filter.pending' },
  { value: 'DONE', labelKey: 'serviceReports.filter.done' },
];

export const WidgetsReportsMain = ({ type }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const canMarkDone = usePerm('reports.mark_done');
  const canViewParcel = usePerm('parcels.detail');

  const [status, setStatus] = useState<ReportStatus | undefined>('PENDING');
  const [doneTarget, setDoneTarget] = useState<ApiServiceReport | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ApiServiceReport | null>(null);

  const query = useReports({ type, status });
  const rows = useMemo(() => query.data ?? [], [query.data]);

  const done = useReportDone();
  const cancel = useReportCancel();

  const columns = useMemo<ColumnDef<ApiServiceReport>[]>(
    () => [
      {
        accessorKey: 'parcel',
        header: t('serviceReports.columns.parcel'),
        cell: (info) => {
          const row = info.row.original;
          if (!row.parcel_id || !canViewParcel) {
            return (
              <span className="block max-w-[200px] truncate font-medium text-ink">
                {row.parcel_display}
              </span>
            );
          }
          return (
            <button
              type="button"
              onClick={() => navigate(`/parcels/${row.parcel_id}`)}
              className="block max-w-[200px] truncate text-left font-medium text-brand-600 hover:underline"
            >
              {row.parcel_display}
            </button>
          );
        },
      },
      {
        accessorKey: 'contact_info',
        header: t('serviceReports.columns.contact'),
        cell: (info) => info.row.original.contact_info,
      },
      {
        accessorKey: 'note',
        header: t('serviceReports.columns.note'),
        cell: (info) => (
          <span className="block max-w-[260px] truncate text-ink">
            {info.row.original.note}
          </span>
        ),
      },
      {
        accessorKey: 'fee',
        header: t('serviceReports.columns.fee'),
        cell: (info) => formatUsd(Number(info.row.original.fee)),
      },
      {
        accessorKey: 'status',
        header: t('serviceReports.columns.status'),
        cell: (info) => {
          const rowStatus = info.row.original.status;
          return (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                rowStatus === 'CANCELLED'
                  ? 'bg-slate-100 text-slate-600'
                  : rowStatus === 'DONE'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700',
              )}
            >
              {rowStatus === 'CANCELLED'
                ? t('serviceReports.status.cancelled')
                : info.row.original.status_label}
            </span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: t('serviceReports.columns.createdAt'),
        cell: (info) => formatDateTime(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('serviceReports.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          if (!canMarkDone) return null;
          const showDone = row.status === 'PENDING';
          const showCancel = row.status === 'PENDING';
          if (!showDone && !showCancel) return null;
          return (
            <div className="flex gap-2">
              {showDone ? (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => setDoneTarget(row)}
                >
                  {t('serviceReports.markDone')}
                </Button>
              ) : null}
              {showCancel ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setCancelTarget(row)}
                >
                  {t('serviceReports.cancel')}
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [t, canMarkDone, canViewParcel, navigate],
  );

  return (
    <>
      <PageHeader title={t(TITLE_KEYS[type].title)} subtitle={t(TITLE_KEYS[type].subtitle)} />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar hideSearch>
          {STATUS_FILTERS.map((filter) => {
            const isActive = status === filter.value;
            return (
              <Button
                key={filter.labelKey}
                variant={isActive ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setStatus(filter.value)}
              >
                {t(filter.labelKey)}
              </Button>
            );
          })}
        </FilterBar>

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('serviceReports.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <DataTable
            data={rows}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle={t('serviceReports.emptyTitle')}
            emptyDescription={t('serviceReports.emptyDescription')}
            emptyIcon={
              type === 'PHOTO' ? (
                <Image className="h-8 w-8" aria-hidden />
              ) : type === 'VIDEO' ? (
                <Video className="h-8 w-8" aria-hidden />
              ) : (
                <Wrench className="h-8 w-8" aria-hidden />
              )
            }
            getRowId={(row) => String(row.id)}
            numbered
          />
        )}
      </div>

      <ConfirmDialog
        open={doneTarget !== null}
        title={t('serviceReports.markDoneTitle')}
        description={
          doneTarget
            ? t('serviceReports.markDoneDescription', { name: doneTarget.parcel_display })
            : ''
        }
        size="md"
        confirmVariant="success"
        confirmLabel={t('serviceReports.markDoneConfirm')}
        cancelLabel={t('common.cancel')}
        loading={done.isPending}
        onCancel={() => setDoneTarget(null)}
        onConfirm={() => {
          if (!doneTarget) return;
          done.mutate(doneTarget.id, {
            onSuccess: () => {
              toast.success(t('serviceReports.toast.done'));
              setDoneTarget(null);
            },
            onError: () => toast.error(t('common.errorGeneric')),
          });
        }}
      >
        <p className="text-sm text-ink-soft">
          {doneTarget?.parcel_display}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={cancelTarget !== null}
        title={t('serviceReports.cancelTitle')}
        description={
          cancelTarget
            ? t('serviceReports.cancelDescription', { name: cancelTarget.parcel_display })
            : ''
        }
        size="md"
        confirmVariant="danger"
        confirmLabel={t('serviceReports.cancelConfirm')}
        cancelLabel={t('common.cancel')}
        loading={cancel.isPending}
        onCancel={() => setCancelTarget(null)}
        onConfirm={() => {
          if (!cancelTarget) return;
          cancel.mutate(cancelTarget.id, {
            onSuccess: () => {
              toast.success(t('serviceReports.toast.cancelled'));
              setCancelTarget(null);
            },
            onError: () => toast.error(t('common.errorGeneric')),
          });
        }}
      >
        <p className="text-sm text-ink-soft">
          {cancelTarget?.parcel_display}
        </p>
      </ConfirmDialog>
    </>
  );
};
